import * as THREE from 'three';
import { ThreeSixtyViewer } from '../ThreeSixtyViewer';
import { ViewerObservation } from '../ViewerElement/ObservationElement';
import { Utilities } from '../Utilities';
import { CameraController } from './CameraController';
import { InteractionHandler } from './InteractionHandler';
import { ViewerElementHandler } from '../ViewerElement/ViewerElementHandler';
import {
  onCameraMoveCallback,
  onCameraZoomCallback,
  ViewerMode,
  ViewerScreenDirection,
  CameraViewport,
  TileManagerConfig,
} from '../Types';
import { ViewportCalculator } from '../ViewportCalculator';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { Walkthrough } from '../ViewerElement/WalkthroughElement';
import { IViewerElement } from '../ViewerElement/ViewerElement';
import { ImageEffects } from '../ImageEffects/ImageEffects';
import { LayerManager, LayerType } from './LayerManager';
import { TileManager } from '../tile-manager';
import { generateLODLevelsFromMetadata } from '../tile-manager/metadataUtils';

export class ViewerScreen {
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  // private sphere: THREE.Mesh;
  private textureLoader: THREE.TextureLoader;
  private container: HTMLElement;
  private readonly DEFAULT_FOV = 80; // Default Field of View for camera

  private isLerping: boolean = false;
  private cameraTarget: THREE.Vector3;
  private lerpSpeed: number = 0.05;
  private observations: ViewerObservation[] = [];
  private walkthrouges: Walkthrough[] = [];
  private raycaster: THREE.Raycaster;
  private sphere: THREE.Mesh;

  private cameraController: CameraController;
  private interactionHandler: InteractionHandler;
  private observationHandler: ViewerElementHandler;
  private walkthroughHandler: ViewerElementHandler;
  public screenDirection: ViewerScreenDirection;
  private imageEffects: ImageEffects;
  private layerManager: LayerManager;
  private tileManager: TileManager;
  private workerUrl: string | undefined;
  private isTilingMode: boolean = false; // Track if using tiling/cube mode or single sphere mode
  private activeLoadToken: number = 0;

  constructor(
    container: HTMLElement,
    renderer: THREE.WebGLRenderer,
    screenDirection: ViewerScreenDirection,
  ) {
    this.raycaster = new THREE.Raycaster();
    this.screenDirection = screenDirection;
    this.container = container;
    this.renderer = renderer;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      this.DEFAULT_FOV,
      this.container.clientWidth / this.container.clientHeight,
      1,
      1100,
    );
    this.camera.position.z = 5;
    this.textureLoader = new THREE.TextureLoader();
    this.cameraTarget = new THREE.Vector3();
    this.cameraController = new CameraController(this.camera, this.scene, this.screenDirection);
    this.interactionHandler = new InteractionHandler(this.scene, this.camera, this.screenDirection);
    this.layerManager = new LayerManager(this.scene);

    // Initialize tile manager for tile-based loading

    this.workerUrl = undefined;

    // Initialize TileManager without config initially - will be updated when metadata is loaded
    this.tileManager = new TileManager(this.scene, this.workerUrl);

    // Initialize image effects
    this.imageEffects = new ImageEffects(this.scene, this.camera, this.renderer);
  }

  private beginNewLoad(): number {
    return ++this.activeLoadToken;
  }

  private isActiveLoad(loadToken: number): boolean {
    return this.activeLoadToken === loadToken;
  }

  private disposeTexture(texture?: THREE.Texture | null): void {
    if (texture) {
      texture.dispose();
    }
  }

  private disposeMeshResources(mesh: THREE.Mesh | null): void {
    if (!mesh) {
      return;
    }

    mesh.geometry?.dispose();

    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    materials.forEach((mat) => {
      if (mat instanceof THREE.Material) {
        const matAny = mat as any;
        if (matAny?.map && typeof matAny.map.dispose === 'function') {
          matAny.map.dispose();
        }
        mat.dispose();
      }
    });
  }

  /**
   * Load metadata.json and configure TileManager for online loading
   */
  private async loadMetadata(metadataUrl?: string): Promise<void> {
    try {
      // Default to local metadata.json if no URL provided
      const url = metadataUrl || 'assets/metadata.json';

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load metadata: ${response.status} ${response.statusText}`);
      }

      const metadata = await response.json();

      // Update existing TileManager with metadata configuration
      const metadataConfig = {
        url: metadata.url,
        levels: metadata.levels,
      };

      // Update the tile manager with metadata
      this.tileManager.updateMetadataConfig(metadataConfig);
    } catch (error) {
      throw error;
    }
  }

  public addCameraMoveListener(listener: onCameraMoveCallback): void {
    this.cameraController.onCameraMoveListeners.push(listener);
  }

  public removeCameraMoveListener(listener: onCameraMoveCallback): void {
    this.cameraController.onCameraMoveListeners =
      this.cameraController.onCameraMoveListeners.filter((l) => l !== listener);
  }
  public addCameraZoomListener(listener: onCameraZoomCallback): void {
    this.cameraController.onCameraZoomListeners.push(listener);
  }
  public removeCameraZoomListener(listener: onCameraZoomCallback): void {
    this.cameraController.onCameraZoomListeners =
      this.cameraController.onCameraZoomListeners.filter((l) => l !== listener);
  }

  /**
   * Displays all observation objects in the scene.
   */
  public showObservations(): void {
    if (this.observationHandler) this.observationHandler.showElements();
  }

  /**
   * Hides all observation objects in the scene.
   */
  public hideObservations(): void {
    if (this.observationHandler) this.observationHandler.hideElements();
  }

  /**
   * Filters observations on the scene based on their status.
   * @param status - The status to filter observations by.
   */
  public filterObservationsByStatus(status: string) {
    if (this.observationHandler) this.observationHandler.filterElementsByStatus(status);
  }

  /**
   * Calculates the position for a new observation based on a mouse event.
   * @param mouse
   * @param newObservationImage
   * @returns The calculated angles for observation positioning.
   */
  public addNewObservation(
    mouse: { x: number; y: number },
    newObservationImage: (rawImage: string) => void,
  ): { horizontalAngle: number; verticalAngle: number } {
    this.raycaster.setFromCamera(mouse, this.camera);
    const observationTargets = this.getObservationTargets();
    const intersects =
      observationTargets.length > 0
        ? this.raycaster.intersectObjects(observationTargets, true)
        : [];

    let clickedPosition = new THREE.Vector3();
    if (intersects.length > 0) {
      const intersect = intersects[0];
      const uv = intersect.uv;
      newObservationImage(this.extractTexturePortion(uv));
      clickedPosition = intersect.point.clone();
    }

    return Utilities.ViewerPositionToKrpanoPosition(clickedPosition);
  }

  public disable() {
    this.camera.visible = false;
  }
  public enable() {
    this.camera.visible = true;
  }
  private getObservationTargets(): THREE.Object3D[] {
    if (this.isTilingMode) {
      return this.tileManager.getTileMeshes();
    }
    return this.sphere ? [this.sphere] : [];
  }
  private extractTexturePortion(uv: THREE.Vector2): string | undefined {
    this.hideObservations(); // to avoid take the observation in the screenshot
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    const { width, height } = this.renderer.getSize(new THREE.Vector2());
    canvas.width = width;
    canvas.height = height;

    const renderTarget = new THREE.WebGLRenderTarget(width, height);
    this.renderer.setRenderTarget(renderTarget);
    this.renderer.render(this.scene, this.camera);
    this.renderer.setRenderTarget(null);

    const pixels = new Uint8Array(width * height * 4); // RGBA format
    this.renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixels);

    if (context) {
      const imageData = context.createImageData(width, height);
      imageData.data.set(pixels);
      context.putImageData(imageData, 0, 0);
      const flippedCanvas = document.createElement('canvas');
      flippedCanvas.width = canvas.width;
      flippedCanvas.height = canvas.height;
      const flippedContext = flippedCanvas.getContext('2d');
      if (flippedContext) {
        flippedContext.translate(0, canvas.height);
        flippedContext.scale(1, -1);
        flippedContext.drawImage(canvas, 0, 0);
      }
      if (ThreeSixtyViewer.isObservationVisible) this.showObservations();
      return flippedCanvas.toDataURL();
    }
  }

  /**
   * Loads a 360° image using tile-based architecture for better performance.
   * Uses tiles data from point image instead of loading metadata.json.
   * @param imageUrl - Base URL for the tile images (from point.image_360).
   * @param angle - Optional initial angles for the camera.
   * @param tiles - Tile levels configuration from point image.
   */
  public async loadImage(
    imageUrl?: string,
    angle?: { verticalAngle?: number; horizontalAngle?: number },
    tiles?: Array<{ level: number; cols_per_face: number; rows_per_face: number }>,
    tileSourceMode: 'standard' | 'blurred' = 'standard',
  ): Promise<void> {
    if (!imageUrl) {
      throw new Error('Image URL is required');
    }

    const hasTiles = Array.isArray(tiles) && tiles.length > 0;

    if (!hasTiles) {
      await this.loadSingleImage(imageUrl, angle);
      return;
    }

    const loadToken = this.beginNewLoad();

    try {
      // Store reference to old sphere to dispose later
      const oldSphere = this.sphere;

      // Set tiling mode flag
      this.isTilingMode = true;

      // Generate LOD levels from the tiles metadata
      const lodLevels = generateLODLevelsFromMetadata(tiles);

      // Configure TileManager with tiles data from point image
      const metadataConfig = {
        url: imageUrl,
        levels: tiles,
        sourceMode: tileSourceMode,
      };

      // If source (base URL) changed, clear scene/cache and reinitialize
      const prevSource = this.tileManager.getSourceKey();
      this.tileManager.updateMetadataConfig(metadataConfig, { sourceMode: tileSourceMode });
      if (!this.isActiveLoad(loadToken)) {
        return;
      }

      // Update LOD levels to match the available tiles
      this.tileManager.setLODLevels(lodLevels);
      if (!this.isActiveLoad(loadToken)) {
        return;
      }

      if (this.tileManager.getSourceKey() !== prevSource) {
        await this.tileManager.forceReload(true);
        if (!this.isActiveLoad(loadToken)) {
          return;
        }
      }

      // Set up camera orientation first
      if (angle?.verticalAngle !== undefined && angle?.horizontalAngle !== undefined) {
        this.cameraController.setInitialCameraOrientation(
          angle.horizontalAngle,
          angle.verticalAngle,
        );
      }

      // Reset FOV to default when loading new image
      this.camera.fov = this.DEFAULT_FOV;

      // Update projection matrix to ensure camera orientation is applied immediately
      this.camera.updateProjectionMatrix();
      if (!this.isActiveLoad(loadToken)) {
        return;
      }

      // Force reload tiles to clear cached tiles at wrong LOD level
      // This ensures tiles are loaded fresh at the correct LOD for the default FOV
      await this.tileManager.forceReload(false);
      if (!this.isActiveLoad(loadToken)) {
        return;
      }

      // Start loading tiles for the current camera viewport
      await this.updateTilesForCurrentViewport();
      if (!this.isActiveLoad(loadToken)) {
        return;
      }

      // Set up camera movement listeners to update tiles
      this.setupCameraListeners();
      
      // Now dispose of old sphere after tiles are loaded
      if (oldSphere) {
        this.scene.remove(oldSphere);
        oldSphere.geometry.dispose();
        if (oldSphere.material instanceof THREE.Material) {
          oldSphere.material.dispose();
        }
        this.sphere = null;
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Load a single equirectangular image without tiles/LOD
   */
  private async loadSingleImage(
    imageUrl: string,
    angle?: { verticalAngle?: number; horizontalAngle?: number },
    loadToken?: number
  ): Promise<void> {
    const token = loadToken ?? this.beginNewLoad();
    const manageLoadingState = loadToken === undefined;
    if (manageLoadingState) {
      ThreeSixtyViewer.notifyLoadingState?.(true, this.screenDirection);
    }
    try {
      // Set single image mode flag
      this.isTilingMode = false;
      
      // Clear any tile meshes/caches by resetting metadata to empty and forcing a reload
      this.tileManager.updateMetadataConfig({ url: '', levels: [] });
      await this.tileManager.forceReload(true);
      if (!this.isActiveLoad(token)) {
        return;
      }

      // Create inward-facing sphere and map texture
      const geometry = new THREE.SphereGeometry(500, 60, 40);
      geometry.scale(-1, 1, 1);

      const texture = await new Promise<THREE.Texture>((resolve, reject) => {
        this.textureLoader.load(
          imageUrl,
          (tex) => resolve(tex),
          undefined,
          (err) => reject(err)
        );
      });
      if (!this.isActiveLoad(token)) {
        this.disposeTexture(texture);
        geometry.dispose();
        return;
      }

      const material = new THREE.MeshBasicMaterial({ map: texture });
      const newSphere = new THREE.Mesh(geometry, material);
      if (!this.isActiveLoad(token)) {
        this.disposeMeshResources(newSphere);
        return;
      }
      
      // Add new sphere to scene before removing old one
      this.scene.add(newSphere);
      
      // Remove old sphere if it exists
      if (this.sphere) {
        this.scene.remove(this.sphere);
        this.sphere.geometry.dispose();
        if (this.sphere.material instanceof THREE.Material) {
          this.sphere.material.dispose();
        }
      }
      
      // Update reference to new sphere
      this.sphere = newSphere;

      // Apply initial camera orientation if provided
      if (angle?.verticalAngle !== undefined && angle?.horizontalAngle !== undefined) {
        this.cameraController.setInitialCameraOrientation(
          angle.horizontalAngle,
          angle.verticalAngle,
        );
      }

      // Reset FOV to default when loading new image
      this.camera.fov = this.DEFAULT_FOV;

      // Update projection matrix to ensure camera orientation is applied immediately
      this.camera.updateProjectionMatrix();

      // Render once to display
      this.renderer.render(this.scene, this.camera);
    } catch (e) {
      throw e;
    } finally {
      if (manageLoadingState && this.isActiveLoad(token)) {
        ThreeSixtyViewer.notifyLoadingState?.(false, this.screenDirection);
      }
    }
  }

  /**
   * Progressive single-image load: show thumb immediately, swap to full-res when ready
   */
  public async loadSingleImageWithThumb(
    thumbUrl: string,
    fullUrl: string,
    angle?: { verticalAngle?: number; horizontalAngle?: number },
    loadToken?: number,
  ): Promise<void> {
    const token = loadToken ?? this.beginNewLoad();
    const manageLoadingState = loadToken === undefined;
    if (manageLoadingState) {
      ThreeSixtyViewer.notifyLoadingState?.(true, this.screenDirection);
    }
    try {
      // Set single image mode flag
      this.isTilingMode = false;
      
      // Reset any tile state
      this.tileManager.updateMetadataConfig({ url: '', levels: [] });
      await this.tileManager.forceReload(true);
      if (!this.isActiveLoad(token)) {
        return;
      }

      // Store reference to old sphere
      const oldSphere = this.sphere;

      // Build sphere once
      const geometry = new THREE.SphereGeometry(500, 60, 40);
      geometry.scale(-1, 1, 1);

      const loadTex = (url: string) => new Promise<THREE.Texture>((resolve, reject) =>
        this.textureLoader.load(url, (t) => resolve(t), undefined, (e) => reject(e))
      );

      // Load thumb first
      const thumbTexture = await loadTex(thumbUrl);
      if (!this.isActiveLoad(token)) {
        this.disposeTexture(thumbTexture);
        geometry.dispose();
        return;
      }
      const material = new THREE.MeshBasicMaterial({ map: thumbTexture });
      const newSphere = new THREE.Mesh(geometry, material);
      if (!this.isActiveLoad(token)) {
        this.disposeMeshResources(newSphere);
        return;
      }
      
      // Add new sphere to scene
      this.scene.add(newSphere);
      
      // Remove old sphere after new one is displayed
      if (oldSphere) {
        this.scene.remove(oldSphere);
        oldSphere.geometry.dispose();
        if (oldSphere.material instanceof THREE.Material) {
          oldSphere.material.dispose();
        }
      }
      
      // Update reference to new sphere
      this.sphere = newSphere;

      if (angle?.verticalAngle !== undefined && angle?.horizontalAngle !== undefined) {
        this.cameraController.setInitialCameraOrientation(
          angle.horizontalAngle,
          angle.verticalAngle,
        );
      }

      // Reset FOV to default when loading new image
      this.camera.fov = this.DEFAULT_FOV;

      // Update projection matrix to ensure camera orientation is applied immediately
      this.camera.updateProjectionMatrix();

      this.renderer.render(this.scene, this.camera);

      if (manageLoadingState) {
        ThreeSixtyViewer.notifyLoadingState?.(false, this.screenDirection);
      }

      // Load full-res in background and swap map when ready
      loadTex(fullUrl).then((hiTex) => {
        if (!this.isActiveLoad(token)) {
          this.disposeTexture(hiTex);
          return;
        }
        // Preserve current camera orientation before swapping texture
        const currentOrientation = this.getCameraOrientation();

        // Dispose old texture
        const mats = Array.isArray(this.sphere.material) ? this.sphere.material : [this.sphere.material];
        for (const m of mats) {
          // @ts-ignore
          if (m?.map && typeof m.map.dispose === 'function') m.map.dispose();
          // @ts-ignore
          m.map = hiTex;
          // @ts-ignore
          m.needsUpdate = true;
        }

        // Restore camera orientation to what the user had set
        if (currentOrientation) {
          this.cameraController.setInitialCameraOrientation(
            currentOrientation.horizontalAngle,
            currentOrientation.verticalAngle,
          );
        }

        // Update projection matrix and render with preserved orientation
        this.camera.updateProjectionMatrix();
        this.renderer.render(this.scene, this.camera);
        if (this.isActiveLoad(token)) {
          ThreeSixtyViewer.notifyLoadingState?.(false, this.screenDirection);
        }
      }).catch(() => {
        if (this.isActiveLoad(token)) {
          ThreeSixtyViewer.notifyLoadingState?.(false, this.screenDirection);
        }
      });
    } catch (e) {
      if (this.isActiveLoad(token)) {
        ThreeSixtyViewer.notifyLoadingState?.(false, this.screenDirection);
      }
      throw e;
    }
  }

  /**
   * Update tiles based on current camera viewport
   */
  public async updateTilesForCurrentViewport(): Promise<void> {
    // Check if metadata is configured before attempting to load tiles
    const metadata = this.tileManager.getMetadataConfig();
    if (!metadata || !metadata.url || metadata.levels.length === 0) {
      // No valid metadata configured yet, skip tile loading
      return;
    }

    const viewport = this.getCurrentCameraViewport();
    await this.tileManager.loadTilesForViewport(viewport);
  }

  /**
   * Get current camera viewport information
   */
  private getCurrentCameraViewport(): CameraViewport {
    const orientation = this.cameraController.getCameraOrientation();
    return {
      fov: this.camera.fov,
      aspect: this.camera.aspect,
      horizontalAngle: orientation.horizontalAngle,
      verticalAngle: orientation.verticalAngle,
      visibleFaces: [], // Will be calculated by ViewportCalculator
    };
  }

  /**
   * Setup camera listeners for tile updates with debounced zoom loading
   */
  private setupCameraListeners(): void {
    // Add camera move listener (immediate updates for panning)
    this.addCameraMoveListener(async () => {
      await this.updateTilesForCurrentViewport();
    });

    // Add camera zoom listener (immediate FOV updates but no tile loading)
    this.addCameraZoomListener(async () => {
      // Only update FOV/LOD calculation, don't load tiles yet
      // Tiles will be loaded when zoom stops
    });

    // Add debounced zoom stopped listener (loads tiles only when zoom stops)
    this.cameraController.addZoomStoppedCallback(async () => {
      await this.updateTilesForCurrentViewport();
    });
  }

  public initObservations(
    observation: ViewerObservation[],
    onObservationClick: (id: string) => void,
  ): void {
    if (this.observationHandler) {
      this.observationHandler.setElements(observation, onObservationClick);
      // Update mode flag
      this.observationHandler.setTilingMode(this.isTilingMode);
    } else {
      this.observationHandler = new ViewerElementHandler(
        this.scene,
        this.camera,
        observation,
        (id: string) => {
          onObservationClick(id);
          // to make sure call onMouseUp event after click
          setTimeout(() => {
            this.onMouseUp();
          }, 100);
        },
        this.layerManager,
        this.isTilingMode,
      );
      this.observations = observation;
      this.interactionHandler.observationHandler = this.observationHandler;
    }
  }

  public initWalkthroughes(
    walkthroughes: Walkthrough[],
    onWalkthroughClicked: (id: string) => void,
  ): void {
    if (this.walkthroughHandler) {
      this.walkthroughHandler.setElements(walkthroughes, onWalkthroughClicked);
      // Update mode flag
      this.walkthroughHandler.setTilingMode(this.isTilingMode);
    } else {
      this.walkthroughHandler = new ViewerElementHandler(
        this.scene,
        this.camera,
        walkthroughes,
        onWalkthroughClicked,
        this.layerManager,
        this.isTilingMode,
      );
    }
    this.walkthrouges = walkthroughes;
    this.interactionHandler.walkthroughHandler = this.walkthroughHandler;
  }

  public unlock(cameraTarget: { lat: number; lon: number; phi: number; theta: number }) {
    this.cameraController.setCameraTarget(cameraTarget);
  }
  public getCameraTarget(): { lat: number; lon: number; phi: number; theta: number } {
    return this.cameraController.getCameraTarget();
  }

  public setDefaultCameraOrientation(angle: { horizontalAngle: number; verticalAngle: number }) {
    this.cameraController.defaultAngle = angle;
  }
  /**
   * Resizes the camera based on the given aspect ratio and updates the scene.
   * @param aspect - The aspect ratio for resizing.
   */
  public resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.observations?.forEach((observation) => {
      observation.element3dObject.updateMatrixWorld();
    });
    this.walkthrouges?.forEach((walkthrough) => {
      walkthrough.element3dObject.updateMatrixWorld();
    });
    this.imageEffects.resize(this.container.clientWidth, this.container.clientHeight);
    
    // If in tiling mode, trigger tile update after resize to account for new aspect ratio
    if (this.isTilingMode) {
      const metadata = this.tileManager.getMetadataConfig();
      if (metadata && metadata.url && metadata.levels.length > 0) {
        // Use a small delay to ensure resize is complete
        setTimeout(() => {
          this.updateTilesForCurrentViewport().catch(() => {
            // Silently handle tile update errors
          });
        }, 100);
      }
    }
  }

  public update(
    angle: { horizontalAngle: number; verticalAngle: number } = null,
    resetCamera: boolean = false,
    fov: number = null,
  ) {
    if (resetCamera) this.cameraController.resetCameraOrientation();
    else if (angle)
      this.cameraController.setInitialCameraOrientation(angle.horizontalAngle, angle.verticalAngle);
    if (fov) this.cameraController.setCameraZoom(fov);
    this.camera.updateProjectionMatrix();
    this.observationHandler?.alignElementsToCamera();

    // If in tiling mode and camera was updated, trigger tile reload
    // Only trigger if metadata is configured (image has been loaded)
    if (this.isTilingMode && (angle || fov || resetCamera)) {
      const metadata = this.tileManager.getMetadataConfig();
      if (metadata && metadata.url && metadata.levels.length > 0) {
        // Use a small delay to ensure camera updates are complete
        setTimeout(() => {
          this.updateTilesForCurrentViewport().catch(() => {
            // Silently handle tile update errors
          });
        }, 100);
      }
    }
  }
  public setCameraZoom(fov: number): void {
    this.cameraController.setCameraZoom(fov);
  }
  public getCameraZoom(): number {
    return this.cameraController.getCameraZoom();
  }
  /**
   * Runs the main animation loop for updating the scene.
   */
  public animate(): void {
    // for auto tour in the future
    // if (this.isUserInteracting === false) {
    //   this.lon += 0.1;
    // }

    if (this.isLerping) {
      this.cameraController.setCameraTarget(this.cameraTarget);
      this.isLerping = false;
    }
    this.observationHandler?.alignElementsToCamera();

    this.scene.traverse((object) => {
      if (object.userData.animate) {
        object.userData.animate();
      }
    });
    this.imageEffects.render();
  }

  public onDestroy(): void {
    // Dispose of sphere (for backward compatibility)
    if (this.sphere) {
      if (this.sphere.geometry) {
        this.sphere.geometry.dispose();
      }
      if (this.sphere.material) {
        (this.sphere.material as THREE.Material).dispose();
      }
      if (this.scene) {
        this.scene.remove(this.sphere);
      }
      this.sphere = null;
    }

    // Dispose of tile manager and all its resources
    if (this.tileManager) {
      this.tileManager.dispose();
    }

    if (this.workerUrl) {
      URL.revokeObjectURL(this.workerUrl);
      this.workerUrl = undefined;
    }

    // Dispose of image effects
    this.imageEffects.dispose();
  }
  public getCameraOrientation(): { horizontalAngle: number; verticalAngle: number } {
    return this.cameraController?.getCameraOrientation();
  }

  public onMouseMove(event: MouseEvent, isCameraOnly: boolean = false): void {
    if (!isCameraOnly) this.interactionHandler?.onMouseMove(event);
    this.cameraController?.onMouseMove(event);
  }
  public onMouseDown(event: MouseEvent, isCameraOnly: boolean = false): void {
    if (!isCameraOnly) this.interactionHandler?.onMouseDown(event);
    this.cameraController?.onMouseDown(event);
  }
  public onMouseUp(): void {
    this.cameraController?.onMouseUp();
  }
  public onMouseWheel(event: WheelEvent): void {
    this.cameraController?.onMouseWheel(event);
  }

  public getImageEffects() {
    return this.imageEffects;
  }

  public setWatermarkEnabled(isEnabled: boolean): void {
    this.imageEffects.setWatermarkEnabled(isEnabled);
  }

  public setWatermarkText(name: string, email: string, client?: string): void {
    this.imageEffects.setWatermarkText(name, email, client);
  }

  public setWatermarkColor(rgb: string): void {
    this.imageEffects.setWatermarkColor(rgb);
  }

  public setWatermarkOpacity(value: number): void {
    this.imageEffects.setWatermarkOpacity(value);
  }

  public setObservations(
    observations: ViewerObservation[],
    onObservationClick: (id: string) => void,
  ): void {
    this.observationHandler = new ViewerElementHandler(
      this.scene,
      this.camera,
      observations,
      onObservationClick,
      this.layerManager,
      this.isTilingMode,
    );
  }

  public setWalkthroughs(
    walkthroughs: Walkthrough[],
    onWalkthroughClick: (id: string) => void,
  ): void {
    this.walkthroughHandler = new ViewerElementHandler(
      this.scene,
      this.camera,
      walkthroughs,
      onWalkthroughClick,
      this.layerManager,
      this.isTilingMode,
    );
  }

  /**
   * Get tile manager cache statistics
   */
  public getTileStats() {
    return this.tileManager.getCacheStats();
  }

  /**
   * Get current LOD level information
   */
  public getCurrentLODLevel() {
    return this.tileManager.getCurrentLODLevel();
  }

  public setLODLevel(level: number): boolean {
    return this.tileManager.setLODLevel(level);
  }

  public getSourceKey(): string {
    return this.tileManager.getSourceKey();
  }

  public getMetadataConfig() {
    return this.tileManager.getMetadataConfig();
  }

  /**
   * Get number of visible tiles
   */
  public getVisibleTilesCount(): number {
    return this.tileManager.getVisibleTilesCount();
  }

  /**
   * Get number of persistent tiles
   */
  public getPersistentTilesCount(): number {
    return this.tileManager.getPersistentTilesCount();
  }

  /**
   * Force reload all tiles (useful for debugging)
   * @param clearPersistent - If true, also clears persistent tiles and reinitializes base tiles
   */
  public async forceReloadTiles(clearPersistent: boolean = false): Promise<void> {
    await this.tileManager.forceReload(clearPersistent);
  }

  /**
   * Add LOD change callback
   */
  public addLODChangeCallback(callback: (oldLevel: number, newLevel: number) => void): void {
    this.tileManager.addLODChangeCallback(callback);
  }

  /**
   * Remove LOD change callback
   */
  public removeLODChangeCallback(callback: (oldLevel: number, newLevel: number) => void): void {
    this.tileManager.removeLODChangeCallback(callback);
  }

  /**
   * Add callback for when zoom stops (debounced)
   */
  public addZoomStoppedCallback(callback: () => void): void {
    this.cameraController.addZoomStoppedCallback(callback);
  }

  /**
   * Remove zoom stopped callback
   */
  public removeZoomStoppedCallback(callback: () => void): void {
    this.cameraController.removeZoomStoppedCallback(callback);
  }

  /**
   * Load texture using THREE.TextureLoader with promise wrapper
   */
  private loadTexture(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false;
          resolve(texture);
        },
        undefined,
        (error) => {
          reject(error);
        },
      );
    });
  }

}
